import { useCallback } from "react";
import { useBenchmarkedFetch } from "./useBenchmarkedFetch";
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const USE_DYNAMSOFT_SCANNER =
  import.meta.env.VITE_USE_DYNAMSOFT_SCANNER === "true";

// Debug logging
console.log("[useValidation] BACKEND_URL:", BACKEND_URL);
console.log("[useValidation] All env vars:", import.meta.env);

export type ValidateResponseType =
  | {
      valid: true;
      label: string;
      redirect_url?: string;
    }
  | {
      valid: false;
      message: string;
    };

export type SerialCodeResponseType = {
  status:
    | "OK"
    | "INVALID_IMAGE_DATA_URI"
    | "FAILED_TO_PROCESS_IMAGE"
    | "NO_TEXT_DATA"
    | "BACKEND_PROCESSING_ERROR";
  message?: string;
  serialCode?: string;
  branding?: unknown;
  redirect_url?: string | null;
  code_status?: string;
};

export const useValidate = () => {
  const { fetch } = useBenchmarkedFetch();
  const lambdaWarmUp = useCallback(async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/health`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
        "Lambda Warm-Up",
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error during Lambda warm-up:", error);
    }
  }, [fetch]);
  const validate = useCallback(
    async (codes: string[]) => {
      const data = {
        codes: codes,
      };
      const url = `${BACKEND_URL}/auth/validate`;
      console.log("[validate] URL:", url);
      console.log("[validate] Payload:", data);
      try {
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        });

        console.log("[validate] Response status:", response.status);
        console.log("[validate] Response headers:", {
          contentType: response.headers.get("content-type"),
          corsOrigin: response.headers.get("access-control-allow-origin"),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error(
            "[useValidation] HTTP error! status:",
            response.status,
            "data:",
            errorData,
          );
          return {
            valid: false,
            message: `Server error: ${response.status}`,
          } as ValidateResponseType;
        }

        const result = await response.json();

        if (result.valid) {
          return {
            valid: true,
            label: "Authenticated",
            redirect_url: result.redirect_url,
          } as ValidateResponseType;
        } else {
          return {
            valid: false,
            message: "Not Authenticated",
          } as ValidateResponseType;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        const errorStack =
          error instanceof Error ? error.stack : "No stack trace";
        console.error("[useValidation] Error validating QR codes:", {
          message: errorMessage,
          stack: errorStack,
          fullError: error,
        });
        return {
          valid: false,
          message:
            "There was an error while validating. Please reload and try again",
        };
      }
    },
    [fetch],
  );

  const getSerialCodeDetails = useCallback(
    async (serialCode: string): Promise<SerialCodeResponseType> => {
      try {
        const response = await fetch(
          `${BACKEND_URL}/auth/v2/serial`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ serialCode }),
          },
          "Get Serial Code Details",
        );

        if (!response.ok) {
          return {
            status: "NO_TEXT_DATA",
            message:
              "No branding information found for the provided serial code.",
          };
        }

        const resultJson = await response.json();
        return {
          status: "OK",
          serialCode: serialCode,
          branding: resultJson["style_details"],
          redirect_url: resultJson["redirectUrl"] || null,
          code_status: resultJson["status"],
        };
      } catch (error) {
        console.error("Error fetching serial code details:", error);
        return {
          status: "BACKEND_PROCESSING_ERROR",
          message: "Failed to fetch serial code details. Please try again.",
        };
      }
    },
    [fetch],
  );

  const extractSerialFromImage = useCallback(
    async (image: string): Promise<SerialCodeResponseType> => {
      const blob = convertDataURIToBlob(image);
      if (!blob) {
        return {
          status: "INVALID_IMAGE_DATA_URI",
          message: "Invalid Image Data URI",
        };
      }

      const formData = new FormData();
      formData.append("image", blob, "image.jpg");

      try {
        const response = await fetch(
          `${BACKEND_URL}/auth/process-image`,
          {
            method: "POST",
            body: formData,
          },
          "Process Image",
        );
        if (!response.ok) {
          return {
            status: "FAILED_TO_PROCESS_IMAGE",
            message: "Failed to process image. Please try again.",
          };
        }

        const resultJson = await response.json();
        const textData = resultJson["serial_code"];
        if (!textData) {
          return {
            status: "NO_TEXT_DATA",
            message: "No text data found in the image.",
          };
        }

        return {
          status: "OK",
          serialCode: textData,
          branding: resultJson["style_details"],
          redirect_url: resultJson["redirectUrl"] || null,
          code_status: resultJson["status"],
        };
      } catch (error) {
        console.error("Error processing image:", error);
        return {
          status: "BACKEND_PROCESSING_ERROR",
          message: "Failed to process image. Please try again.",
        };
      }
    },
    [fetch],
  );

  const getSerialCodeFromImage = useCallback(
    async (image: string): Promise<SerialCodeResponseType> => {
      /*
            If we're using Dynamsoft, we already have the serial code (the string input), so 
            we should instead call /auth/v2/serial, which will return the branding info if it exists,
            or an error if it doesn't.

            If we're not using Dynamsoft, we continue to use the v1 endpoint which extracts the serial code 
            from the image via textract.
        */
      if (!USE_DYNAMSOFT_SCANNER) return extractSerialFromImage(image);
      return getSerialCodeDetails(image);
    },
    [extractSerialFromImage, getSerialCodeDetails],
  );

  return { validate, getSerialCodeFromImage, lambdaWarmUp };
};

const convertDataURIToBlob = (dataUri: string) => {
  const data = dataUri.split(",")[1]; // splits to extract only base64 data
  const mimeType = dataUri.split(";")[0]?.slice(5); // extracts image type
  if (!data || !mimeType) {
    console.error("Invalid data URI:", dataUri);
    return null;
  }
  const bytes = window.atob(data); // decodes binary64 into raw binary data
  const buf = new ArrayBuffer(bytes.length);
  const byteArr = new Uint8Array(buf); // handles raw bytes of the image

  for (let i = 0; i < bytes.length; i++) {
    byteArr[i] = bytes.charCodeAt(i);
  }
  return new Blob([byteArr], { type: mimeType });
};
